import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";

const SCHEMAS = {
  site_copy: [{k:"key",l:"Key"},{k:"value",l:"Value",long:true}],
  testimonials: [{k:"author",l:"Author"},{k:"role",l:"Role"},{k:"quote",l:"Quote",long:true}],
  faqs: [{k:"question",l:"Question"},{k:"answer",l:"Answer",long:true}],
  posts: [{k:"slug",l:"Slug"},{k:"title",l:"Title"},{k:"excerpt",l:"Excerpt",long:true},{k:"body_md",l:"Body (Markdown)",long:true,rows:10},{k:"cover",l:"Cover image URL or data:URL"}],
  areas: [{k:"name",l:"Name"},{k:"region",l:"Region"},{k:"postcodes",l:"Postcodes (comma-sep)"}],
  landing_sections: [{k:"section",l:"Section key (hero/standard/...)",},{k:"title",l:"Title"},{k:"body",l:"Body",long:true}],
};
const TABS = Object.keys(SCHEMAS);

// Maps a CMS collection to the public page where its content appears.
const PREVIEW_PATHS = {
  landing_sections: "/",
  testimonials: "/",
  faqs: "/faqs",
  site_copy: "/",
  areas: "/",
};

function CmsTable({ collection }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const reload = () => api.get(`/admin/cms/${collection}`).then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [collection]);

  const blank = () => SCHEMAS[collection].reduce((a, f) => ({...a, [f.k]: ""}), {status:"draft"});
  const save = async () => {
    const { id, status, ...rest } = editing;
    const payload = { data: rest, status: status || "draft" };
    try {
      if (id) await api.patch(`/admin/cms/${collection}/${id}`, payload);
      else await api.post(`/admin/cms/${collection}`, payload);
      toast.success("Saved");
      setOpen(false); reload();
    } catch (e) { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/admin/cms/${collection}/${id}`);
    toast.success("Deleted"); reload();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={`cms-new-${collection}`} onClick={() => setEditing(blank())} className="rounded-full bg-[#2A2624] hover:bg-[#1E1A18]">+ New</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Edit" : "New"} {collection}</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
              {SCHEMAS[collection].map(f => (
                <div key={f.k}>
                  <label className="od-label">{f.l}</label>
                  {f.long
                    ? <Textarea rows={f.rows || 3} value={editing?.[f.k] || ""} onChange={e => setEditing({...editing, [f.k]: e.target.value})} className="mt-1 rounded-xl border-[#E2DBD6]" />
                    : <Input value={editing?.[f.k] || ""} onChange={e => setEditing({...editing, [f.k]: e.target.value})} className="mt-1 rounded-xl border-[#E2DBD6]" />}
                </div>
              ))}
              <div>
                <label className="od-label">Status</label>
                <Select value={editing?.status || "draft"} onValueChange={v => setEditing({...editing, status: v})}>
                  <SelectTrigger className="mt-1 rounded-xl border-[#E2DBD6]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid={`cms-save-${collection}`} onClick={save} className="w-full rounded-full bg-[#2A2624] hover:bg-[#1E1A18] mt-4">Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="od-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4EFEA] text-left">
            <tr>{SCHEMAS[collection].slice(0,2).map(f => <th key={f.k} className="p-3">{f.l}</th>)}<th className="p-3">Status</th><th className="p-3 w-32"></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-[#E2DBD6]">
                {SCHEMAS[collection].slice(0,2).map(f => <td key={f.k} className="p-3 truncate max-w-xs">{String(it[f.k] || "—").slice(0,80)}</td>)}
                <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full ${it.status==="published"?"bg-[#2A2624]/15 text-[#2A2624]":"bg-[#E2DBD6]"}`}>{it.status}</span></td>
                <td className="p-3 text-right space-x-2">
                  {PREVIEW_PATHS[collection] && (
                    <a
                      href={PREVIEW_PATHS[collection]}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`cms-preview-${collection}`}
                      className="text-xs od-link"
                    >Preview</a>
                  )}
                  <button onClick={() => { setEditing(it); setOpen(true); }} className="text-xs od-link">Edit</button>
                  <button onClick={() => remove(it.id)} className="text-xs text-[#B04A4A]">Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#78716C]">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminCms() {
  usePageTitle("Content studio — Olivia Does");
  return (
    <div data-testid="admin-cms-page" className="max-w-[1400px] mx-auto px-6 md:px-8 py-10">
      <span className="od-label">Content management</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Olivia content studio</h1>
      <p className="text-[#78716C] mt-2 max-w-2xl">Edit every piece of customer-facing copy without touching code. Draft work safely, publish when ready.</p>
      <Tabs defaultValue={TABS[0]} className="mt-10">
        <TabsList className="bg-[#F4EFEA] rounded-full p-1 flex-wrap h-auto">
          {TABS.map(t => <TabsTrigger key={t} value={t} data-testid={`cms-tab-${t}`} className="rounded-full data-[state=active]:bg-[#2A2624] data-[state=active]:text-white capitalize">{t.replace("_"," ")}</TabsTrigger>)}
        </TabsList>
        {TABS.map(t => <TabsContent key={t} value={t} className="mt-6"><CmsTable collection={t} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
